<template>
  <div>
    <div class="wz-page-header">
      <!-- wz-page-header es flex-column (título + subtítulo); anidamos un flex-row para
           poner el título a la izquierda y el panel de columnas a la derecha, en la misma fila. -->
      <div class="flex items-center justify-between">
        <h1 class="wz-page-title mb-0">Contratos</h1>
        <!-- El panel hardcodea su tooltip arriba (se corta contra el borde). Lo suprimimos
             (tooltip-text="") y lo reemplazamos por un wz-tooltip envolvente a la izquierda.
             El botón usa su variant default primary-soft (celeste suave + ícono azul, sin borde,
             hover saturado), sin overrides — consistente con los botones-ícono del toolbar. -->
        <wz-tooltip text="Configurar columnas" position="left">
          <wz-columns-preferences-panel
            tooltip-text=""
            :columns="columns"
            :hidden-columns="hiddenColumns"
            @toggle="toggleColumn"
            @reorder="reorderColumns"
            @reset="resetColumns"
          />
        </wz-tooltip>
      </div>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Reporte de seguros">
            <wz-button variant="outline" @click="downloadInsuranceReport()">
              <i class="ri-shield-check-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por número de documento o nombres y apellidos"
            @search="onSearch"
            @input="search = $event"
          />
          <div class="cargar-dropdown" ref="cargarDropdown">
            <wz-button variant="outline" @click.stop="toggleCargarMenu">
              <i class="ri-upload-2-line mr-1"></i>Cargar
              <i class="ri-arrow-down-s-line ml-1"></i>
            </wz-button>
            <div v-if="showCargarMenu" class="cargar-menu">
              <button type="button" class="cargar-menu-item" @click="goBulkUpload">
                <i class="ri-upload-2-line mr-2"></i>Cargar contratos
              </button>
              <button type="button" class="cargar-menu-item" @click="goBulkEdit">
                <i class="ri-edit-box-line mr-2"></i>Edición masiva
              </button>
              <button type="button" class="cargar-menu-item" @click="goBulkAddenda">
                <i class="ri-file-copy-2-line mr-2"></i>Adenda masiva
              </button>
            </div>
          </div>
          <wz-button @click="$router.push({ name: 'contracts-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList || employeeIdFilter">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
          <wz-badge
            v-if="employeeIdFilter"
            variant="primary"
            pill
            dismissible
            @dismiss="clearEmployeeFilter"
          >
            Colaborador: {{ employeeFilterName || ('#' + employeeIdFilter) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <!-- Columna de expansión de adendas: primera, sticky a la izquierda (sobrevive el scroll horizontal). -->
            <th class="addenda-arrow-col"></th>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              :class="[{
                'wz-th-sortable': col.sortKey,
                'is-sorted': col.sortKey && sortKey === col.sortKey,
              }]"
              @click="col.sortKey ? toggleSort(col.sortKey) : null"
            >
              {{ col.label }}
              <i v-if="col.sortKey" :class="['wz-th-sort-icon', sortIcon(col.sortKey)]"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="o in sortedList(list)">
          <tr :key="o.id">
            <!-- Flecha de expandir: solo si el contrato tiene adendas (addendaCount >= 1). -->
            <td class="addenda-arrow-col">
              <button
                v-if="o.addendaCount"
                type="button"
                class="addenda-arrow-btn"
                :aria-expanded="isExpanded(o.id) ? 'true' : 'false'"
                :title="isExpanded(o.id) ? 'Ocultar adendas' : ('Ver adendas (' + o.addendaCount + ')')"
                @click="toggleExpand(o)"
              >
                <i :class="isExpanded(o.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
              </button>
            </td>
            <td v-for="col in visibleColumns" :key="col.key" :style="cellStyle(col.key)">
              <template v-if="col.key === 'number'">
                {{ o.number }}
                <wz-tooltip :text="o.hasSignedDocument ? 'Contrato firmado adjunto' : 'Sin documento firmado'">
                  <i
                    :class="[
                      'ml-1',
                      o.hasSignedDocument ? 'ri-file-check-line text-success' : 'ri-close-circle-line text-danger',
                    ]"
                    style="font-size: 14px;"
                  ></i>
                </wz-tooltip>
              </template>

              <template v-else-if="col.key === 'employeeCode'">{{ o.employeeCode || '-' }}</template>
              <template v-else-if="col.key === 'fullName'">{{ o.fullName || '-' }}</template>
              <template v-else-if="col.key === 'company'">{{ o.company || '-' }}</template>
              <template v-else-if="col.key === 'area'">{{ o.area || '-' }}</template>
              <template v-else-if="col.key === 'channel'">{{ o.channelName || '-' }}</template>
              <template v-else-if="col.key === 'expenseLine'">{{ o.expenseLineName || '-' }}</template>

              <template v-else-if="col.key === 'startDate'">
                <span v-local-date="o.startDate"></span>
              </template>

              <template v-else-if="col.key === 'finishDate'">
                <span v-if="o.finishDate" v-local-date="o.finishDate"></span>
                <span v-else>Indeterminado</span>
              </template>

              <template v-else-if="col.key === 'contractType'">{{ getContractType(o.contractType) }}</template>

              <template v-else-if="col.key === 'baseSalary'">
                <span v-decimal:pen="o.baseSalary" v-if="o.baseSalary"></span>
                <span v-else>-</span>
              </template>


              <template v-else-if="col.key === 'status'">
                <wz-badge :variant="contractStatusVariant(o)" pill>
                  {{ getContractStatus(o).label }}
                </wz-badge>
                <wz-badge v-if="isInProbation(o)" variant="warning-solid" pill class="ml-1">
                  En prueba
                </wz-badge>
                <wz-badge v-if="o.hasRegisteredRenewal" variant="success" pill class="ml-1">
                  RENOVADO
                </wz-badge>
              </template>

              <template v-else-if="col.key === 'options'">
                <wz-dropdown
                  :items="contractActions(o)"
                  placement="left"
                  @click="handleContractAction(o, $event)"
                />
              </template>
            </td>
          </tr>
          <!-- Fila expandida: adendas del contrato (lazy-load + caché). Ocupa todo el ancho. -->
          <tr v-if="isExpanded(o.id)" :key="o.id + '-addenda'" class="addenda-expanded-row">
            <td :colspan="visibleColumns.length + 1">
              <addenda-inline-table
                :contract-id="o.id"
                :addenda="addendaCache[o.id] || []"
                :loading="!!addendaLoading[o.id]"
              />
            </td>
          </tr>
          </template>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length"
          :total="total"
          :current-page="page"
          :per-page="pageSize"
          @change="onPaginationChange"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>

    <input
      ref="signedFileInput"
      type="file"
      accept=".pdf,.jpg,.jpeg,.png"
      style="display: none"
      @change="onSignedFileSelected"
    />

    <wz-modal v-model="modalCease" title="Cesar contrato">
      <div v-if="selectedOption">
        <p class="mb-3">
          Vas a cesar el contrato <strong>{{ selectedOption.number }}</strong> de
          <strong>{{ selectedOption.fullName }}</strong>. Esta acción es irreversible.
        </p>

        <div class="mb-3">
          <label class="font-medium">Fecha de cese <span class="text-danger">*</span></label>
          <wz-datepicker v-model="ceaseForm.terminationDate" :max="todayIso()" />
        </div>

        <div class="mb-3">
          <label class="font-medium">Tipo de cese <span class="text-danger">*</span></label>
          <wz-select
            v-model="ceaseForm.terminationType"
            :items="reasonOptions"
            defaultOption="Seleccionar tipo"
            @change="onCeaseTypeChange"
          />
        </div>

        <div class="mb-3">
          <label class="font-medium">Motivo de cese</label>
          <wz-select
            v-model="ceaseForm.cessationReasonId"
            :items="cessationReasonOptions"
            :disabled="!ceaseForm.terminationType"
            :defaultOption="ceaseForm.terminationType ? 'Seleccionar motivo (opcional)' : 'Seleccione un tipo primero'"
          />
        </div>

        <div class="mb-3">
          <label class="font-medium">Observaciones</label>
          <wz-textarea v-model="ceaseForm.terminationNotes" rows="5" maxlength="1000" />
          <div class="text-right text-sm" :style="{ color: ceaseNotesCounterColor }">
            {{ (ceaseForm.terminationNotes || '').length }} / 1000
          </div>
        </div>

        <div class="mb-3">
          <label class="font-medium">Documentos de cese
            <small class="text-muted">(PDF, JPG, PNG — máx. 4 archivos, 10MB c/u)</small>
          </label>
          <wz-file-input
            id-file="cease-docs-upload"
            :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
            @onChange="onDocsSelected"
          />
          <ul v-if="ceaseForm.documents.length" class="mt-2" style="list-style:none; padding:0; margin:0;">
            <li
              v-for="(doc, idx) in ceaseForm.documents"
              :key="idx"
              class="flex items-center justify-between text-sm py-1"
            >
              <span><i class="ri-file-line mr-1"></i>{{ doc.name }}</span>
              <button type="button" class="border-0 bg-transparent text-danger" @click="removeDoc(idx)">
                <i class="ri-close-line"></i>
              </button>
            </li>
          </ul>
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalCease = false">Cancelar</wz-button>
        <wz-button
          variant="danger"
          :disabled="!ceaseForm.terminationDate || !ceaseForm.terminationType"
          @click="ceaseContract"
        >Cesar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: generar documento desde plantilla -->
    <wz-modal v-model="modalGenerate" title="Generar contrato" size="lg" :close-on-overlay="false">
      <div v-if="genTemplates.length === 0" class="text-muted">
        No hay plantillas configuradas para este tipo de contrato. Solicite a un administrador subir una.
      </div>
      <div v-else>
        <wz-select
          label="Plantilla"
          v-model="selectedTemplateId"
          :items="genTemplates.map(t => ({ key: t.id, label: t.hasVariant ? (t.name + ' ✨ (personalizada)') : t.name }))"
          defaultOption="Seleccionar plantilla"
        />
        <wz-alert v-if="selectedTemplateHasVariant" variant="info" icon="ri-information-line" class="mt-2">
          Se generará usando la versión personalizada para este colaborador.
        </wz-alert>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalGenerate = false">Cancelar</wz-button>
        <wz-button
          v-if="genTemplates.length > 0"
          variant="primary"
          :disabled="!selectedTemplateId || generating"
          @click="doGenerate"
        >{{ generating ? 'Generando...' : 'Generar' }}</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { PaginationMixin } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants, downloadErrorMessage } from '@/core/utils'
import { ContractsService, AreaService, CompanyService, CessationReasonService } from '@/core/services'
import ColumnPreferencesService from '@/core/services/ColumnPreferencesService'
import ContractAddendumService from '@/core/services/ContractAddendumService'
import AddendaInlineTable from '@/modules/Contracts/Pages/components/AddendaInlineTable.vue'
import fileDownload from 'js-file-download'
import { draggableColumnsMixin } from '@wizepe/wizeui'

// Orden por defecto. Todas las columnas son reordenables por drag & drop (incluida "Opciones").
function defaultColumns() {
  return [
    { key: 'number',       label: 'Número de contrato',     sortKey: 'number',          fixed: false },
    { key: 'employeeCode', label: 'Código',                 sortKey: 'employeeCode',    fixed: false },
    { key: 'fullName',     label: 'Nombre del colaborador', sortKey: 'fullName',        fixed: false },
    { key: 'company',      label: 'Empresa',                sortKey: 'company',         fixed: false },
    { key: 'area',         label: 'Área',                   sortKey: 'area',            fixed: false },
    { key: 'channel',      label: 'Canal',                  sortKey: 'channelName',     fixed: false },
    { key: 'expenseLine',  label: 'Línea de gasto',         sortKey: 'expenseLineName', fixed: false },
    { key: 'startDate',    label: 'Fecha de inicio',        sortKey: 'startDate',       fixed: false },
    { key: 'finishDate',   label: 'Fecha de fin',           sortKey: 'finishDate',      fixed: false },
    { key: 'contractType', label: 'Tipo',                   sortKey: 'contractType',    fixed: false },
    { key: 'baseSalary',   label: 'Sueldo base',            sortKey: 'baseSalary',      fixed: false },
    { key: 'status',       label: 'Estado',                 sortKey: 'displayStatus',   fixed: false },
    { key: 'options',      label: 'Opciones',               sortKey: null,              fixed: true },
  ]
}

export default {
  mixins: [SortMixin, PaginationMixin, FilterMixin, draggableColumnsMixin],
  components: { AddendaInlineTable },
  props: {
    // Servicio de preferencias por usuario (backend). Override del prop del mixin con default,
    // ya que esta página no recibe props de un padre.
    preferencesService: { type: Object, default: () => ColumnPreferencesService },
  },
  data() {
    return {
      list: [],
      // Expansión de adendas inline por fila: qué contratos están expandidos + caché y loading por contrato.
      expandedIds: {},
      addendaCache: {},
      addendaLoading: {},
      showCargarMenu: false,
      columnsStorageKey: 'crystal_contract_columns_order',
      columnsListKey: 'contracts',
      tooltipType: null,
      selectedOption: null,
      signedUploadContractId: null,
      // Filtro por colaborador (desde la ficha del colaborador, vía query param)
      employeeIdFilter: null,
      employeeFilterName: '',
      // Filtro "operativo" del drill-down "sin firma": excluye POR INICIAR. Fuera de `filters` a
      // propósito (no es filterOption), para que el menú de wizeui no lo descarte al reescribir.
      excludeNotStarted: false,
      modalCease: false,
      modalGenerate: false,
      genTemplates: [],
      selectedTemplateId: null,
      generating: false,
      genContractId: null,
      ceaseForm: {
        terminationDate: null,
        terminationType: null,
        cessationReasonId: null,
        terminationNotes: '',
        documents: [],
      },
      reasonOptions: [],
      cessationReasonOptions: [],
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          // Valores String coherentes con displayStatus (filtro jerárquico: VIGENTE incluye POR VENCER).
          options: [
            { key: 'VIGENTE',    label: 'VIGENTE' },
            { key: 'POR_VENCER', label: 'POR VENCER' },
            { key: 'RENOVADO',   label: 'RENOVADO' }, // ocultos por defecto: solo con este filtro
            { key: 'VENCIDO',    label: 'VENCIDO' },
            { key: 'CESADO',     label: 'CESADO' },
          ],
        },
        {
          label: 'Fecha de inicio del contrato',
          key: 'contractInitiation',
          type: 'DATE',
          isRange: true,
        },
        {
          label: 'Fecha de término del contrato',
          key: 'contractFinish',
          type: 'DATE',
          isRange: true,
        },
        {
          label: 'Empresa',
          key: 'companyIds',
          type: 'MULTIPLE',
          options: [],
        },
        {
          label: 'Área',
          key: 'areaIds',
          type: 'MULTIPLE',
          options: [],
        },
        {
          label: 'Tipo de contrato',
          key: 'contractType',
          type: 'SINGLE',
          options: Object.values(LocalConstants.ContractType).map(t => ({ key: t.key, label: t.label })),
        },
        {
          label: 'Vida Ley',
          key: 'hasLifeInsurance',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Sí' },
            { key: 'false', label: 'No' },
          ],
        },
        {
          label: 'SCTR Pensión',
          key: 'hasSctrPension',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Sí' },
            { key: 'false', label: 'No' },
          ],
        },
        {
          label: 'SCTR Salud',
          key: 'hasSctrHealth',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Sí' },
            { key: 'false', label: 'No' },
          ],
        },
        {
          label: 'EPS',
          key: 'hasEps',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Sí' },
            { key: 'false', label: 'No' },
          ],
        },
        {
          label: 'Período de prueba',
          key: 'probation',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Sí' },
            { key: 'false', label: 'No' },
          ],
        },
        {
          label: 'Documento firmado',
          key: 'hasSignedDocument',
          type: 'SINGLE',
          options: [
            { key: 'true', label: 'Firmado' },
            { key: 'false', label: 'Sin firma' },
          ],
        },
      ],
    }
  },
  computed: {
    ceaseNotesCounterColor() {
      const len = (this.ceaseForm.terminationNotes || '').length
      if (len >= 1000) return '#dc3545' // rojo
      if (len >= 900) return '#e8a33d'  // amarillo/naranja
      return '#6c757d'                  // gris
    },
    // La plantilla seleccionada tiene una variante personalizada para este colaborador.
    selectedTemplateHasVariant() {
      const t = this.genTemplates.find(x => x.id === this.selectedTemplateId)
      return !!(t && t.hasVariant)
    },
  },
  methods: {
    // Dropdown "Cargar": abre/cierra menú, cierra al elegir opción o al hacer click fuera.
    toggleCargarMenu() {
      if (this.showCargarMenu) { this.closeCargarMenu(); return }
      this.showCargarMenu = true
      document.addEventListener('click', this.handleCargarOutside)
    },
    closeCargarMenu() {
      this.showCargarMenu = false
      document.removeEventListener('click', this.handleCargarOutside)
    },
    handleCargarOutside(e) {
      const el = this.$refs.cargarDropdown
      if (el && !el.contains(e.target)) this.closeCargarMenu()
    },
    goBulkUpload() {
      this.closeCargarMenu()
      this.$router.push({ name: 'contracts-bulk-upload' })
    },
    goBulkEdit() {
      this.closeCargarMenu()
      this.$router.push({ name: 'contracts-bulk-edit' })
    },
    goBulkAddenda() {
      this.closeCargarMenu()
      this.$router.push({ name: 'contracts-addenda-bulk' })
    },
    // Estilo por celda; el drag/persistencia/reset vienen de draggableColumnsMixin.
    cellStyle(key) {
      return key === 'options' ? 'text-align: center;' : ''
    },
    // --- Adendas inline (fila expandible) ---
    isExpanded(id) {
      return !!this.expandedIds[id]
    },
    toggleExpand(o) {
      if (!o.addendaCount) return
      if (this.expandedIds[o.id]) {
        this.$set(this.expandedIds, o.id, false)
        return
      }
      this.$set(this.expandedIds, o.id, true)
      // Lazy-load con caché: solo golpea el backend la primera vez que se expande.
      if (this.addendaCache[o.id] === undefined) this.loadAddenda(o.id)
    },
    async loadAddenda(contractId) {
      try {
        this.$set(this.addendaLoading, contractId, true)
        const resp = await ContractAddendumService.list(contractId)
        this.$set(this.addendaCache, contractId, resp.payload || [])
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las adendas')
        // Deja [] en caché para no reintentar en bucle; el usuario puede recargar la página.
        this.$set(this.addendaCache, contractId, [])
      } finally {
        this.$set(this.addendaLoading, contractId, false)
      }
    },
    contractActions(o) {
      const actions = [
        { key: 'detail',   label: 'Ver detalle', icon: 'ri-eye-line' },
        { key: 'edit',     label: 'Editar' },
        { key: 'document', label: 'Generar documento' },
        { key: 'upload-signed', label: o.hasSignedDocument ? 'Reemplazar contrato firmado' : 'Subir contrato firmado' },
      ]
      if (o.hasSignedDocument) {
        actions.push({ key: 'download-signed', label: 'Descargar contrato firmado' })
      }
      if (this.canRenew(o)) {
        actions.push({ key: 'renew', label: 'Renovar', icon: 'ri-refresh-line' })
      }
      if (this.canCease(o)) {
        actions.push({ key: 'cease', label: 'Cesar', danger: true })
      }
      return actions
    },
    handleContractAction(contract, item) {
      if (item.key === 'detail')   this.$router.push({ name: 'contracts-detail', params: { id: contract.id } })
      if (item.key === 'edit')     this.$router.push({ name: 'contracts-edit', params: { id: contract.id } })
      if (item.key === 'document') this.openGenerateModal(contract)
      if (item.key === 'renew')    this.$router.push({ name: 'contracts-create', query: { renewFromId: contract.id } })
      if (item.key === 'cease')    this.openCeaseModal(contract)
      if (item.key === 'upload-signed') this.triggerSignedUpload(contract)
      if (item.key === 'download-signed') this.downloadSignedDoc(contract)
    },
    async downloadSignedDoc(contract) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ContractsService.downloadSignedDocument(contract.id)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el contrato firmado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    triggerSignedUpload(contract) {
      if (contract.hasSignedDocument && !confirm('¿Desea reemplazar el documento firmado actual?')) return
      this.signedUploadContractId = contract.id
      this.$refs.signedFileInput.click()
    },
    async onSignedFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 100 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 100MB')
        this.$refs.signedFileInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        await ContractsService.uploadSignedDocument(this.signedUploadContractId, file)
        Alert.success('Documento firmado adjuntado correctamente')
        await this.loadData()
      } catch (error) {
        console.error(error)
        Alert.error('Error al subir el documento firmado')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.signedFileInput.value = ''
        this.signedUploadContractId = null
      }
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
      this.loadData()
    },
    contractStatusVariant(o) {
      const map = {
        'VIGENTE':     'success-solid',
        'POR_INICIAR': 'info-solid',    // azul: contrato futuro (aún no arranca)
        'POR_VENCER':  'warning-solid',
        'VENCIDO':     'danger-solid',
        'CESADO':      'secondary-solid',
      }
      return map[o.displayStatus] || 'dark'
    },
    isInProbation(o) {
      // En prueba: contrato vigente (VIGENTE o POR_VENCER) con fin de prueba aún vigente (hoy <= probationEndDate)
      if (!o.probationEndDate || !['VIGENTE', 'POR_VENCER'].includes(o.displayStatus)) return false
      const today = new Date().toISOString().split('T')[0]
      return o.probationEndDate >= today
    },
    async loadCessationTypes() {
      try {
        this.reasonOptions = (await CessationReasonService.listTypes()).payload || []
      } catch (e) {
        console.error(e)
      }
    },
    // Aplana los filtros de rango DATE al shape que espera el backend (@RequestBody FilterContractRequest):
    // campos planos ISO contractInitiationStart/End (inicio) y contractFinishStart/End (término).
    // WzFilterMenu guarda cada rango como objeto { start, end }; ese objeto lo descarta Jackson
    // (@JsonIgnoreProperties(ignoreUnknown=true)), por lo que sin aplanar el filtro nunca llega al backend.
    buildFilterPayload() {
      const data = { search: this.search, ...this.filters }
      const flattenRange = (key, fromField, toField) => {
        const range = this.filters[key]
        delete data[key]
        if (range && range.start) data[fromField] = range.start
        if (range && range.end)   data[toField]   = range.end
      }
      flattenRange('contractInitiation', 'contractInitiationStart', 'contractInitiationEnd')
      flattenRange('contractFinish',     'contractFinishStart',     'contractFinishEnd')
      return data
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { page: this.page - 1, size: this.pageSize, sort: 'id' }
        const data = this.buildFilterPayload()
        if (this.employeeIdFilter) data.employeeId = this.employeeIdFilter
        if (this.excludeNotStarted) data.excludeNotStarted = true // fuera de `filters`; boolean real
        const response = await ContractsService.list(data, params)
        // Nueva data (filtros/paginación/recarga): limpiar expansión y caché de adendas para no mostrar
        // filas expandidas huérfanas ni adendas obsoletas.
        this.expandedIds = {}
        this.addendaCache = {}
        this.addendaLoading = {}
        this.list = response.payload.content.map(item => ({ ...item, showMobileData: false }))
        this.total = response.payload.totalElements
        this.pages = response.payload.totalPages
        // Nombre del colaborador para el chip de filtro (todas las filas son del mismo)
        if (this.employeeIdFilter && this.list.length) this.employeeFilterName = this.list[0].fullName
      } catch (e) {
        console.error(e)
        Alert.error(e.errors.message)
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    clearEmployeeFilter() {
      this.employeeIdFilter = null
      this.employeeFilterName = ''
      this.page = 1
      // Quitar el query param para que un refresh no vuelva a aplicar el filtro
      const query = { ...this.$route.query }
      delete query.employeeId
      this.$router.replace({ query }).catch(() => {})
      this.loadData()
    },
    getContractStatus(contract) {
      switch (contract.displayStatus) {
        case 'POR_INICIAR': return { label: 'POR INICIAR', color: 'info-solid' }
        case 'POR_VENCER':  return { label: 'POR VENCER',  color: 'warning-solid' }
        case 'VIGENTE':     return { label: 'VIGENTE',     color: 'success-solid' }
        case 'VENCIDO':     return { label: 'VENCIDO',     color: 'danger-solid' }
        case 'CESADO':      return { label: 'CESADO',      color: 'secondary-solid' }
        default:            return { label: '-',           color: 'dark' }
      }
    },
    getContractType(key) {
      if (!key) return '-'
      const item = Object.values(LocalConstants.ContractType).find(t => t.key === key)
      return item ? item.label : key
    },
    canCease(o) {
      if (!o) return false
      // POR INICIAR (contrato futuro): solo cesable si es la renovación de otro contrato (o.isRenewal),
      // para poder anular la renovación B antes de que arranque. Un futuro sin origen NO se cesa.
      if (o.displayStatus === 'POR_INICIAR') return o.isRenewal === true
      // Vigente por fecha (VIGENTE o POR_VENCER), aún no cesado: se mantiene como estaba.
      return ['VIGENTE', 'POR_VENCER'].includes(o.displayStatus)
    },
    // Renovable: contrato a plazo fijo (no indeterminado '01') VIGENTE, POR_VENCER o VENCIDO.
    canRenew(o) {
      if (!o) return false
      const isActive = o.displayStatus === 'VIGENTE'
      const byVencer = o.displayStatus === 'POR_VENCER'
      const byVencido = o.displayStatus === 'VENCIDO'
      const isIndefinite = o.contractType === '01'
      return (isActive || byVencer || byVencido) && !isIndefinite
    },
    openCeaseModal(o) {
      this.selectedOption = o
      this.ceaseForm = {
        terminationDate: this.todayIso(),
        terminationType: null,
        cessationReasonId: null,
        terminationNotes: '',
        documents: [],
      }
      this.cessationReasonOptions = []
      this.modalCease = true
    },
    async onCeaseTypeChange(type) {
      this.ceaseForm.terminationType = type
      this.ceaseForm.cessationReasonId = null
      this.cessationReasonOptions = []
      if (!type) return
      try {
        const resp = await CessationReasonService.listByType(type)
        this.cessationReasonOptions = (resp.payload || []).map(r => ({
          key: r.id,
          label: r.name,
        }))
      } catch (e) {
        console.error(e)
      }
    },
    onDocsSelected(files) {
      if (!files) return
      Array.from(files).forEach(f => {
        if (this.ceaseForm.documents.length >= 4) {
          Alert.error('Máximo 4 documentos de cese')
          return
        }
        if (f.size > 10 * 1024 * 1024) {
          Alert.error(`${f.name} supera 10MB`)
          return
        }
        if (!this.ceaseForm.documents.some(d => d.name === f.name && d.size === f.size)) {
          this.ceaseForm.documents.push(f)
        }
      })
    },
    removeDoc(idx) {
      this.ceaseForm.documents.splice(idx, 1)
    },
    todayIso() {
      const d = new Date()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    async ceaseContract() {
      if (!this.selectedOption) return
      if (!this.ceaseForm.terminationDate || !this.ceaseForm.terminationType) {
        Alert.error('Completa la fecha y el tipo de cese')
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('terminationDate', this.ceaseForm.terminationDate)
        fd.append('terminationType', this.ceaseForm.terminationType)
        if (this.ceaseForm.cessationReasonId) fd.append('cessationReasonId', this.ceaseForm.cessationReasonId)
        if (this.ceaseForm.terminationNotes) fd.append('terminationNotes', this.ceaseForm.terminationNotes)
        this.ceaseForm.documents.forEach(f => fd.append('documents', f))
        await ContractsService.cease(this.selectedOption.id, fd)
        this.modalCease = false
        await this.loadData()
        Alert.success('Contrato cesado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cesar el contrato')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async downloadDocument(contractId) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ContractsService.downloadDocument(contractId)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al generar el documento del contrato')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async openGenerateModal(contract) {
      this.genContractId = contract.id
      this.selectedTemplateId = null
      this.genTemplates = []
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await ContractsService.applicableTemplates(contract.id)
        this.genTemplates = resp.payload || []
        this.modalGenerate = true
      } catch (error) {
        Alert.error(error.errors ? error.errors.message : 'Error al cargar las plantillas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async doGenerate() {
      try {
        this.generating = true
        const genResp = await ContractsService.generateDocument(this.genContractId, this.selectedTemplateId)
        const documentId = genResp.payload && genResp.payload.id
        const dl = await ContractsService.downloadGeneratedDocument(this.genContractId, documentId)
        fileDownload(dl.data, Util.getFileName(dl))
        Alert.success('Documento generado')
        this.modalGenerate = false
      } catch (error) {
        Alert.error(error.errors ? error.errors.message : 'Error al generar el documento')
      } finally {
        this.generating = false
      }
    },
    async downloadReport() {
      try {
        await this.$store.dispatch('app/loading', true)
        const data = this.buildFilterPayload()
        if (this.excludeNotStarted) data.excludeNotStarted = true // consistente con la lista visible
        const response = await ContractsService.download(data)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error(await downloadErrorMessage(error) || 'Error al descargar el reporte')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async downloadInsuranceReport() {
      try {
        await this.$store.dispatch('app/loading', true)
        // Decisión (a): solo pasar companyId si hay exactamente una empresa en el filtro
        const ids = this.filters.companyIds
        const companyId = Array.isArray(ids) && ids.length === 1 ? ids[0] : null
        const response = await ContractsService.downloadInsuranceReport(companyId)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el reporte de seguros')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async getAreas() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await AreaService.listAll()).payload
        const optionItem = this.filterOptions.find(item => item.key === 'areaIds')
        optionItem.options = resp.map(item => ({ key: item.id, label: item.description }))
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async getCompanies() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await CompanyService.listAll()).payload
        const optionItem = this.filterOptions.find(item => item.key === 'companyIds')
        optionItem.options = resp.map(item => ({ key: item.id, label: item.name }))
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    this.initColumns(defaultColumns(), this.columnsStorageKey, this.columnsListKey)
    const eid = this.$route.query.employeeId
    if (eid) this.employeeIdFilter = Number(eid)
    // Filtros provenientes de un drill-down del dashboard: estado y empresa. El route trae companyId
    // (singular); el backend filtra por companyIds (lista), así que lo mapeamos.
    const qStatus = this.$route.query.status
    const qCompanyId = this.$route.query.companyId
    const qHasSigned = this.$route.query.hasSignedDocument
    if (qStatus) this.$set(this.filters, 'status', qStatus)
    if (qCompanyId) this.$set(this.filters, 'companyIds', [Number(qCompanyId)])
    // Viene como string 'true'/'false' en el route; el dropdown y el backend lo interpretan igual.
    if (qHasSigned !== undefined) this.$set(this.filters, 'hasSignedDocument', String(qHasSigned))
    // excludeNotStarted se maneja FUERA de `filters` (como employeeIdFilter): el menú de wizeui solo
    // preserva keys que son filterOption, así que dentro de `filters` se perdería al reescribir.
    // Tolerante a boolean (primer navigation vía router.push: $route.query trae el boolean true) y a
    // string (tras recarga F5: la URL se parsea a 'true'). Con === 'true' fallaba en la primera carga.
    const qExcludeNotStarted = this.$route.query.excludeNotStarted
    this.excludeNotStarted = qExcludeNotStarted === true || qExcludeNotStarted === 'true'
    await this.getCompanies()
    await this.getAreas()
    await this.loadCessationTypes()
    await this.loadData(this.page, this.pageSize)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleCargarOutside)
  },
}
</script>

<style lang="stylus">
// Columna de expansión de adendas: fija (sticky) a la izquierda para que sobreviva el scroll horizontal.
.wz-table th.addenda-arrow-col,
.wz-table td.addenda-arrow-col
  position sticky
  left 0
  z-index 3
  width 40px
  min-width 40px
  max-width 40px
  padding 0
  text-align center
  background var(--admin-bg-panel, #fff)

.wz-table th.addenda-arrow-col
  z-index 4

.addenda-arrow-btn
  display inline-flex
  align-items center
  justify-content center
  width 28px
  height 28px
  border none
  border-radius 6px
  background transparent
  color var(--admin-text-main)
  cursor pointer
  font-size 18px
  line-height 1

  &:hover
    background var(--admin-bg-hover)

.addenda-expanded-row > td
  padding 0
  background var(--admin-bg-muted, #f8f9fa)

.col-draggable
  cursor move

.cargar-dropdown
  position relative
  display inline-block

.cargar-menu
  position absolute
  top calc(100% + 4px)
  right 0
  z-index 99999
  min-width 190px
  padding 4px
  background var(--admin-bg-panel)
  border 1px solid var(--admin-border-color)
  border-radius 0.375rem
  box-shadow 0 4px 12px rgba(0, 0, 0, 0.12)

.cargar-menu-item
  display flex
  align-items center
  width 100%
  padding 8px 12px
  border none
  border-radius 0.25rem
  background transparent
  color var(--admin-text-main)
  font-size 0.875rem
  text-align left
  cursor pointer

.cargar-menu-item:hover
  background-color var(--admin-bg-hover)
</style>
